import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  CommonModule,
  DatePipe,
  CurrencyPipe,
  DecimalPipe,
} from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatNativeDateModule,
  MatPseudoCheckboxModule,
  MatRippleModule,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { MarkersComponent } from './components/markers/markers.component';
import { ProgressComponent } from './components/progress/progress.component';
import { AlertComponent } from './components/alert/alert.component';
import { ModalComponent } from './components/modal/modal.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { TableComponent } from './components/table/table.component';
import { FormatDataPipe } from './pipes/format-data.pipe';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { TooltipDirective } from './directives/tooltip.directive';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { MenuItemCardComponent } from './components/menu-item-card/menu-item-card.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { QuillModule } from 'ngx-quill';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToastrModule } from 'ngx-toastr';
import { OrderByPipe } from './pipes/order-by.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DragDropDirective } from './directives/drag-drop.directive';
import { UploadComponent } from './components/upload/upload.component';
import { FileSizePipe } from './pipes/filesize.pipe';
import { InvoiceStatePipe } from './pipes/invoice-state.pipe';
import { quillConfig } from '@app/core/config/quill-config.model';
import { DynamicPipe } from './pipes/dynamic.pipe';
import { MoneyPipe } from './pipes/money.pipe';
import { BooleanPipe } from './pipes/boolean.pipe';
import { DelayPipe } from './pipes/delay.pipe';
import { JoinListPipe } from './pipes/join-list.pipe';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DecimalFormatInputDirective } from './directives/decimalInput.directive';
import { ActivityOperation } from './pipes/activity-operation.pipe';
import { ActivityDomaine } from './pipes/activity-domaine.pipe';
import { HeaderFriendlyNamePipe } from './pipes/header-friendly-name.pipe';
import { FrenchPaginatorIntl } from './components/table/french-paginator-intl';
import { NullableBooleanPipe } from './pipes/nullable-boolean.pipe';
import { AttachmentComponent } from './components/attachment/attachment.component';
import { WorkflowStageValidationComponent } from '@app/features/workflow-stage-validation/workflow-stage-validation.component';
import { MatRadioModule } from '@angular/material/radio';
import { PdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import { NumberCommaToDotFormatPipe } from './pipes/number-comma-to-dot-format.pipe';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatCardModule } from '@angular/material/card';
import { YearMonthFormatDirective } from './directives/yearMonthFormat.directive';
import { DashboardItemCardComponent } from './components/dashboard-item-card/dashboard-item-card.component';
import { CustomDateRangeInputControl } from './components/custom-date-range-input/custom-date-range-input.component';
import { HeaderFormComponent } from './components/header-form/header-form.component';
import { CustomAmountRangeInputComponent } from './components/custom-amount-range-input/custom-amount-range-input.component';
import { WriteCommentComponent } from './components/write-comment/write-comment.component';
import { EllipsisPipe } from './pipes/ellipsis.pipe';
import { PrimeNgModule } from './primeng.modules';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [
    NotFoundComponent,
    DropdownComponent,
    TableComponent,
    FormatDataPipe,
    InvoiceStatePipe,
    OrderByPipe,
    NullableBooleanPipe,
    HeaderFriendlyNamePipe,
    DynamicPipe,
    ActivityDomaine,
    ActivityOperation,
    DragDropDirective,
    DecimalFormatInputDirective,
    AlertComponent,
    MarkersComponent,
    ModalComponent,
    ProgressComponent,
    TooltipDirective,
    MenuItemCardComponent,
    RichTextEditorComponent,
    UploadComponent,
    FileSizePipe,
    AttachmentComponent,
    WorkflowStageValidationComponent,
    PdfViewerComponent,
    NumberCommaToDotFormatPipe,
    YearMonthFormatDirective,
    DashboardItemCardComponent,
    CustomDateRangeInputControl,
    HeaderFormComponent,
    CustomAmountRangeInputComponent,
    WriteCommentComponent,
    EllipsisPipe,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatCardModule,
    MatPseudoCheckboxModule,
    MatNativeDateModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSortModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatFormFieldModule,
    DragDropModule,
    MatInputModule,
    MatDatepickerModule,
    MatDialogModule,
    NgMultiSelectDropDownModule.forRoot(),
    MatSlideToggleModule,
    PdfJsViewerModule,
    FlexLayoutModule,
    QuillModule.forRoot(quillConfig),
    ToastrModule.forRoot(),
    PrimeNgModule,
  ],
  exports: [
    NotFoundComponent,
    DropdownComponent,
    AlertComponent,
    ProgressComponent,
    ModalComponent,
    MarkersComponent,
    TableComponent,
    FormatDataPipe,
    InvoiceStatePipe,
    NullableBooleanPipe,
    OrderByPipe,
    HeaderFriendlyNamePipe,
    DragDropDirective,
    DecimalFormatInputDirective,
    YearMonthFormatDirective,
    TooltipDirective,
    MenuItemCardComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatPseudoCheckboxModule,
    MatNativeDateModule,
    MatRippleModule,
    MatSelectModule,
    MatSortModule,
    MatTooltipModule,
    MatMenuModule,
    MatCardModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatDatepickerModule,
    PdfJsViewerModule,
    DragDropModule,
    RichTextEditorComponent,
    MatIconModule,
    MatCheckboxModule,
    UploadComponent,
    FileSizePipe,
    DynamicPipe,
    ActivityDomaine,
    ActivityOperation,
    WorkflowStageValidationComponent,
    PdfViewerComponent,
    NumberCommaToDotFormatPipe,
    DashboardItemCardComponent,
    FlexLayoutModule,
    CustomDateRangeInputControl,
    CustomAmountRangeInputComponent,
    HeaderFormComponent,
    WriteCommentComponent,
    PrimeNgModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MatPaginatorIntl, useValue: FrenchPaginatorIntl() },
    DatePipe,
    BooleanPipe,
    NullableBooleanPipe,
    DecimalPipe,
    InvoiceStatePipe,
    DynamicPipe,
    ActivityDomaine,
    ActivityOperation,
    FormatDataPipe,
    CurrencyPipe,
    MoneyPipe,
    HeaderFriendlyNamePipe,
    DelayPipe,
    JoinListPipe,
    NumberCommaToDotFormatPipe,
  ],
})
export class SharedModule {}
